/**
 * Chackerheader Component
 * Animated "hacker-style" header that types out text with delete/write animation.
 * Text changes trigger a delete animation of the current text followed by typing of the new text.
 */

'use client';

import type { ReactElement } from 'react';
import { ColorType } from '@/theme/colors';
import { FontWeight } from '@/theme/typography';
import { Temper, getTemperColor } from '@/types/frontend-types';
import { ChackerheaderView } from './ChackerheaderView';
import { useHackerHeaderAnimation } from './useHackerHeaderAnimation';

export interface ChackerheaderProps {
  /** The text to display (changes trigger delete/type animation) */
  text: string;
  /** Font size of the text (default: 21) */
  fontSize?: number;
  /** Gap between brackets and text (default: 50) */
  gap?: number | string;
  /** Temper controls text color: good=accent, neutral=white, bad=danger (default: good) */
  temper?: Temper;
  /** Override font weight (default: FontWeight.SemiBold) */
  fontWeight?: FontWeight;
  /** Override opacity (default: 1) */
  opacity?: number;
  /** Override color (overrides temper if provided) */
  color?: ColorType;
}

export function Chackerheader({ 
  text, 
  fontSize = 21,
  gap = 50,
  temper = Temper.Good,
  fontWeight = FontWeight.SemiBold,
  opacity = 1,
  color 
}: ChackerheaderProps): ReactElement {
  const textColor = color !== undefined ? color : ColorType[getTemperColor(temper)];
  const { displayText, isAnimating } = useHackerHeaderAnimation({ text });

  return (
    <ChackerheaderView
      displayText={displayText}
      fontSize={fontSize}
      fontWeight={fontWeight}
      gap={gap}
      isAnimating={isAnimating}
      opacity={opacity}
      textColor={textColor}
    />
  );
}
